import React, { useEffect, useRef, useState } from "react";
import BaseTable from "../components/shared/BaseTable";
import { Box, Button } from "@mui/material";
import { InboundStudiesList, InboundSearchStudy } from "../api/Inbound";
import { GetAllPatient, SearchInboundPatients } from "../api/Patient";
import api from "../api/config";
import PatientNotFoundModal from "../components/modals/PatientNotFoundModal";
import SubmitModal from "../components/modals/SubmitModal";

const InboundStudies = () => {
  const columns = [
    { id: "first_name", label: "First Name" },
    { id: "last_name", label: "Last Name" },
    { id: "date_of_birth", label: "Birth Date" },
    { id: "gender", label: "Sex" },
  ];
  const studyColumns = [
    { id: "patientName", label: "Name" },
    { id: "dob", label: "Birth Date" },
    { id: "patientGender", label: "Sex" },
    { id: "status", label: "Status" },
    { id: "transfer_status", label: "status", display: "hidden" },
    { id: "transfer_progress", label: "progress", display: "hidden" },
  ];

  const [patientData, setPatientData] = useState([]);
  const [studyData, setStudyData] = useState([]);
  const [studyLoading, setStudyLoading] = useState(false);
  const [patientLoading, setPatientLoading] = useState(false);
  const [selectedPatientItems, setSelectedPatientItems] = useState([]);
  const [selectedStudyItems, setSelectedStudyItems] = useState([]);
  const [studySearch, setStudySearch] = useState("");
  const [patientSearch, setPatientSearch] = useState("");
  const [studyPage, setStudyPage] = useState(0);
  const [studyLimit, setStudyLimit] = useState(10);
  const [studyTotalRecords, setStudyTotalRecords] = useState(0);
  const fetchAbortController = useRef(null);
  const searchAbortController = useRef(null);
  const fetchPatientAbortController = useRef(null);
  const searchPatientAbortController = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [patientPage, setPatientPage] = useState(0);
  const [patientLimit, setPatientLimit] = useState(10);
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [patientTotalRecords, setPatientTotalRecords] = useState(0);
  const [isPatientNotFoundOpen, setIsPatientNotFoundOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

  const handleOpenPatientNotFound = () => setIsPatientNotFoundOpen(true);
  const handleClosePatientNotFound = () => setIsPatientNotFoundOpen(false);

  const handleOpenSubmitModal = () => setIsSubmitModalOpen(true);
  const handleCloseSubmitModal = () => setIsSubmitModalOpen(false);

  const cancelPreviousPatientRequest = (controller) => {
    if (controller.current) {
      controller.current.abort();
    }
    controller.current = new AbortController();
  };

  const cancelPreviousRequest = (controller) => {
    if (controller.current) {
      controller.current.abort();
    }
    controller.current = new AbortController();
  };

  const fetchStudies = async () => {
    cancelPreviousRequest(fetchAbortController);
    setStudyLoading(true);
    console.log("Fetching studies...");

    try {
      const offset = studyPage * studyLimit;

      const response = await api.get(
        `${InboundStudiesList}?offset=${offset}&limit=${studyLimit}`,
        { signal: fetchAbortController.current.signal }
      );

      console.log("Response received:", response.data);

      const transformedData = (response.data.studies || []).map((study) => ({
        ...study,
        id: study.studyUID,
        dob: formatDate(study.patientDOB),
      }));

      setStudyData(transformedData);
      setStudyTotalRecords(response.data.pagination.total || 0);
      setStudyLoading(false);
    } catch (error) {
      console.error("Error in fetchStudies:", error);
      if (error.name !== "CanceledError") {
        setStudyLoading(false);
      }
    }
  };

  const handleStudySearch = async (query) => {
    if (!query || query.length < 3) {
      return;
    }

    cancelPreviousRequest(fetchAbortController);
    setSearchQuery(query);

    cancelPreviousRequest(searchAbortController);
    setStudyLoading(true);

    try {
      const response = await api.get(
        `${InboundSearchStudy}?patient_name=${query}`,
        { signal: searchAbortController.current.signal }
      );

      const transformedData = (response.data.studies || []).map((study) => ({
        ...study,
        id: study.studyUID,
        dob: formatDate(study.patientDOB),
      }));

      setStudyData(transformedData);
      setStudyTotalRecords(transformedData.length);
      setStudyLoading(false);
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error in search:", error);
        setStudyLoading(false);
      }
    }
  };

  const fetchPatients = async () => {
    cancelPreviousPatientRequest(fetchPatientAbortController);
    setPatientLoading(true);

    try {
      const offset = patientPage * patientLimit;
      let clientUuid = localStorage.getItem(`clientuuid`);
      const response = await api.get(
        `${GetAllPatient}${clientUuid}?offset=${offset}&limit=${patientLimit}`,
        {
          signal: fetchPatientAbortController.current.signal,
        }
      );

      console.log("Response:", response.data);

      const transformedData = (response.data.patients || []).map((patient) => ({
        ...patient,
        id: patient.uuid,
      }));

      setPatientData(transformedData);
      setPatientTotalRecords(response.data.total_patients || 0);
      setPatientLoading(false);
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error fetching patients:", error);
        setPatientLoading(false);
      }
    }
  };

  const formatDate = (yyyymmdd) => {
    if (!yyyymmdd || yyyymmdd.length !== 8) return yyyymmdd;
    const year = yyyymmdd.substring(0, 4);
    const month = yyyymmdd.substring(4, 6);
    const day = yyyymmdd.substring(6, 8);
    return `${month}-${day}-${year}`;
  };

  const handlePatientSearch = async (query) => {
    if (query !== patientSearchQuery) {
      setPatientPage(0);
    }

    const cleanQuery = query.replace(/\^+/g, " ").trim();

    const parts = cleanQuery.split(" ");

    const filteredQuery =
      parts.length >= 2 ? `${parts[0]} ${parts[1]}` : cleanQuery;

    setPatientSearchQuery(filteredQuery);

    if (!query || query.length < 3) {
      fetchPatients();
      return;
    }

    cancelPreviousPatientRequest(searchPatientAbortController);
    setPatientLoading(true);

    try {
      const offset = patientPage * patientLimit;
      const response = await api.get(
        `${SearchInboundPatients}?name=${query}&offset=${offset}&limit=${patientLimit}`,
        { signal: searchPatientAbortController.current.signal }
      );

      const transformedData = (response.data.patients || []).map((patient) => ({
        ...patient,
        id: patient.uuid,
      }));

      setPatientData(transformedData);
      setPatientTotalRecords(response.data.total_patients || 0);
      setPatientLoading(false);
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error in patient search:", error);
        setPatientLoading(false);
        resetPatientSearch();
      }
    }
  };

  const resetPatientSearch = () => {
    setPatientSearchQuery("");
    setPatientData([]);
    setPatientTotalRecords(0);
  };

  const handleStudyClearFilters = () => {
    if (searchQuery) {
      cancelPreviousRequest(searchAbortController);
      setSearchQuery("");
      // fetchStudies();
    }
  };

  const handlePatientClearFilters = () => {
    if (patientSearchQuery) {
      setPatientSearchQuery("");
      setPatientPage(0);
      fetchPatients();
    }
  };

  const handleStudyRefresh = async () => {
    if (searchQuery) {
      setStudySearch(searchQuery);
      handleStudySearch(searchQuery);
    } else {
      fetchStudies();
    }
  };

  const handleStudyRowSelect = (selectedRows) => {
    if (!selectedRows.length) return;

    console.log("Selected Rows:", selectedRows);

    const selectedStudy = selectedRows[0];
    setSelectedStudyItems(selectedRows);

    if (!selectedStudy.patientName) return;

    setStudySearch("");
    setPatientSearch("");

    const cleanQuery = selectedStudy.patientName.replace(/\^+/g, " ").trim();

    const parts = cleanQuery.split(" ");

    const filteredQuery =
      parts.length >= 2 ? `${parts[0]} ${parts[1]}` : cleanQuery;

    setTimeout(() => {
      setStudySearch(selectedStudy.patientName);
      setPatientSearch(filteredQuery);
    }, 100);
  };

  const handlePatientRefresh = async () => {
    if (patientSearchQuery) {
      setPatientSearch(patientSearchQuery);
      handlePatientSearch(patientSearchQuery);
    } else {
      fetchPatients();
    }
  };

  const handlePatientRowSelect = (selectedRows) => {
    setSelectedPatientItems(selectedRows);
    console.log("Currently Selected Rows:", selectedRows);
  };

  useEffect(() => {
    console.log("Mounting component... fetching studies and patients");
    setStudySearch("");
    setPatientSearch("");
    setSelectedStudyItems([]);
    setSelectedPatientItems([]);
    fetchStudies();
    fetchPatients();
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      fetchStudies();
    }
  }, [studyPage, studyLimit, searchQuery]);

  useEffect(() => {
    if (patientSearchQuery) {
      handlePatientSearch(patientSearchQuery);
    } else {
      fetchPatients();
    }
  }, [patientPage, patientLimit]);

  return (
    <Box sx={{ width: "100%", maxWidth: "100vw", overflowX: "hidden" }}>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}
      >
        <Button
          variant="contained"
          color="error"
          onClick={handleOpenPatientNotFound}
        >
          Patient Not Found
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenSubmitModal}
        >
          Submit
        </Button>
        <PatientNotFoundModal
          open={isPatientNotFoundOpen}
          onClose={handleClosePatientNotFound}
          selectedStudies={selectedStudyItems}
          selectedPatients={selectedPatientItems}
        />

        <SubmitModal
          open={isSubmitModalOpen}
          onClose={handleCloseSubmitModal}
          selectedStudies={selectedStudyItems}
          selectedPatients={selectedPatientItems}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "stretch",
          gap: "20px",
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
        }}
      >
        <Box sx={{ flex: 1, maxWidth: "50%" }}>
          <BaseTable
            title="Studies List"
            columns={studyColumns}
            data={studyData}
            isLoading={studyLoading}
            onSearch={handleStudySearch}
            onSort={(column, direction) =>
              console.log("Sorting by:", column, direction)
            }
            multiSelect={true}
            selectableRows={true}
            onRowSelect={handleStudyRowSelect}
            onRefresh={handleStudyRefresh}
            onPageChange={(newPage, newLimit) => {
              setStudyPage(newPage);
              setStudyLimit(newLimit);
            }}
            totalRecords={studyTotalRecords}
            page={studyPage}
            limit={studyLimit}
            onClearFilters={handleStudyClearFilters}
            forcedSearchQuery={studySearch}
          />
        </Box>
        <Box sx={{ flex: 1, maxWidth: "50%" }}>
          <BaseTable
            title="Patients List"
            columns={columns}
            data={patientData}
            isLoading={patientLoading}
            onSearch={handlePatientSearch}
            onSort={(column, direction) =>
              console.log("Sorting by:", column, direction)
            }
            selectableRows={true}
            onRowSelect={handlePatientRowSelect}
            onRefresh={handlePatientRefresh}
            onPageChange={(newPage, newLimit) => {
              setPatientPage(newPage);
              setPatientLimit(newLimit);
            }}
            totalRecords={patientTotalRecords}
            page={patientPage}
            limit={patientLimit}
            onClearFilters={handlePatientClearFilters}
            forcedSearchQuery={patientSearch}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default InboundStudies;
